import { useEffect, useState, useCallback, useRef } from "react";
import api, { eur, num } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ItalyMap from "@/components/ItalyMap";
import ProfilazioneRete from "@/components/ProfilazioneRete";
import AgencyTrendChart from "@/components/AgencyTrendChart";
import AgencyScorecard from "@/components/AgencyScorecard";
import ImpegniChiusura from "@/components/ImpegniChiusura";
import { PianoPresidioDialog } from "@/pages/ObiettiviAgenzie";
import QuadrantChart from "@/components/QuadrantChart";
import { Euro, Users, Building2, AlertTriangle, Trophy, Loader2, Network, RefreshCw, Handshake, UserCheck, CalendarRange, Percent, ArrowUp, ArrowDown, ArrowUpDown, UserX, ChevronDown, LayoutGrid, ListOrdered, Route } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { agentColorHex } from "@/lib/agentColors";

const AGENCY_COLORS = ["#0EA5E9", "#0284C7", "#38BDF8", "#0369A1", "#22D3EE", "#60A5FA", "#2563EB", "#7DD3FC"];
const pad2 = (n) => String(n).padStart(2, "0");
const fmtOrderDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
const ymOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
const monthsAgo = (n) => { const d = new Date(); d.setMonth(d.getMonth() - (n - 1)); d.setDate(1); return ymOf(d); };
const buildQuery = (p, f, t) => {
  if (!p || p === "all") return "";
  const now = ymOf(new Date());
  if (p === "last2") return `?from_month=${new Date().getFullYear() - 1}-01&to_month=${now}`;
  if (p === "m3") return `?from_month=${monthsAgo(3)}&to_month=${now}`;
  if (p === "m6") return `?from_month=${monthsAgo(6)}&to_month=${now}`;
  if (p === "m12") return `?from_month=${monthsAgo(12)}&to_month=${now}`;
  if (p === "custom") {
    const parts = [];
    if (f) parts.push(`from_month=${f}`);
    if (t) parts.push(`to_month=${t}`);
    return parts.length ? `?${parts.join("&")}` : "";
  }
  return `?year=${p}`;
};

const KPI = ({ icon: Icon, label, value, accent, testid, legend }) => (
  <Card className="p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`flex h-8 w-8 items-center justify-center rounded-md ${accent ? "bg-primary text-primary-foreground" : "bg-accent"}`}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
    <div className="mt-3 font-heading text-3xl font-black tracking-tight">{value}</div>
    {legend && <div className="mt-2 border-t border-border pt-2 text-[10px] leading-tight text-muted-foreground">{legend}</div>}
  </Card>
);

const YoySlot = ({ title, subtitle, block, testid, period, onOrders }) => {
  const pct = block?.delta_pct;
  const up = pct != null && pct >= 0;
  const Trend = up ? ArrowUp : ArrowDown;
  const trendColor = pct == null ? "text-muted-foreground" : up ? "text-emerald-400" : "text-red-400";
  return (
    <div
      className="relative w-full rounded-xl border-2 border-primary bg-primary/10 p-6 text-left"
      data-testid={testid}
    >
      {block?.media_giorno != null && (
        <span className="absolute -top-3 left-4 z-10 rounded-md bg-primary px-2.5 py-0.5 text-[11px] font-black uppercase tracking-wide text-primary-foreground shadow-md" data-testid={`${testid}-media`} title={`Media giornaliera ${block?.year_curr}: ${eur(block?.curr ?? 0)} ÷ ${block?.giorni} giorni`}>
          Ø {eur(block?.media_giorno)}/gg · {block?.giorni} gg
        </span>
      )}
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-heading text-sm font-black uppercase tracking-wide text-primary">{title}</div>
          <div className="text-[11px] text-muted-foreground">{subtitle}</div>
        </div>
        <div className={`flex items-center gap-1 rounded-md px-2 py-1 text-sm font-black ${trendColor} ${pct == null ? "bg-muted/40" : up ? "bg-emerald-500/15" : "bg-red-500/15"}`} data-testid={`${testid}-delta`}>
          <Trend className="h-4 w-4" />
          {pct == null ? "N/D" : `${up ? "+" : ""}${pct}%`}
        </div>
      </div>
      <div className="mt-3 flex flex-col gap-1 xl:flex-row xl:items-end xl:justify-between xl:gap-2">
        <div className="min-w-0">
          <div className="font-heading text-2xl font-black tracking-tight sm:text-3xl" data-testid={`${testid}-curr`}>{eur(block?.curr ?? 0)}</div>
          <div className="text-[11px] text-muted-foreground">{block?.year_curr}</div>
        </div>
        <div className="min-w-0 xl:text-right">
          <div className="font-mono text-base font-semibold text-muted-foreground" data-testid={`${testid}-prev`}>{eur(block?.prev ?? 0)}</div>
          <div className="text-[11px] text-muted-foreground">{block?.year_prev}</div>
        </div>
      </div>
      <div className={`mt-1 text-[11px] font-semibold ${trendColor}`}>
        {block?.delta != null ? `${block.delta >= 0 ? "+" : ""}${eur(block.delta)} vs ${block?.year_prev}` : "—"}
      </div>
      {block?.margine != null && (
        <div className="mt-1 text-[11px] font-semibold text-muted-foreground" data-testid={`${testid}-margine`}>Margine di commessa: <span className="text-foreground">{eur(block.margine)}</span> · {block.margine_pct}%{block.margine_no_dati ? <span className="font-normal text-muted-foreground/80"> · {block.margine_no_dati} senza margine</span> : null}</div>
      )}
      <button type="button" onClick={() => onOrders?.(period)} className="mt-2 inline-flex items-center gap-1 rounded-md text-[11px] font-semibold text-primary underline-offset-2 transition-colors hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary" data-testid={`${testid}-ordini-link`}>
        <ListOrdered className="h-3.5 w-3.5" /> n. {block?.n_ordini ?? 0} ordini ricevuti
      </button>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isAgency = user?.role === "agency";
  const [data, setData] = useState(null);
  const [pianoOpen, setPianoOpen] = useState(false);
  const [ordini, setOrdini] = useState(null);
  const [rankSort, setRankSort] = useState({ key: "vesta_punti", dir: "desc" });
  const [expandedTerr, setExpandedTerr] = useState({});
  const [terrOpen, setTerrOpen] = useState(false);
  const [error, setError] = useState(false);
  const [period, setPeriod] = useState(null); // null = bootstrap
  const [fromM, setFromM] = useState("");
  const [toM, setToM] = useState("");
  const [scatterPartners, setScatterPartners] = useState([]);
  const [trendView, setTrendView] = useState("periodo");
  const trendRef = useRef(null);
  const openOrdini = useCallback((period) => {
    setOrdini({ period, loading: true, data: null });
    api.get(`/dashboard/ordini?period=${period}`)
      .then((r) => setOrdini({ period, loading: false, data: r.data }))
      .catch(() => setOrdini({ period, loading: false, data: { orders: [], count: 0, total: 0 } }));
  }, []);
  useEffect(() => { api.get("/partners").then((r) => setScatterPartners(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    // Apertura automatica del Piano di presidio per l'agente, solo la prima volta per sessione
    if (isAgency && user?.agency_id && !sessionStorage.getItem("piano_autoopen_shown")) {
      sessionStorage.setItem("piano_autoopen_shown", "1");
      setPianoOpen(true);
    }
  }, [isAgency, user?.agency_id]);

  const fetchData = useCallback((p, f, t) => {
    setError(false);
    setData(null);
    api.get(`/dashboard${buildQuery(p, f, t)}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, []);

  useEffect(() => {
    api.get("/dashboard").then((r) => {
      const yrs = (r.data.trend_years || []).map(String);
      const cur = String(new Date().getFullYear());
      const target = yrs.includes(cur) ? cur : (yrs.length ? yrs[yrs.length - 1] : "all");
      setPeriod(target);
      if (target === "all") setData(r.data);
      else fetchData(target);
    }).catch(() => setError(true));
  }, [fetchData]);

  const changePeriod = (p) => { setPeriod(p); if (p !== "custom") fetchData(p, fromM, toM); };
  const applyCustom = () => fetchData("custom", fromM, toM);
  const load = () => fetchData(period, fromM, toM);

  if (error)
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-center" data-testid="dashboard-error">
        <AlertTriangle className="h-8 w-8 text-destructive" />
        <div>
          <p className="font-heading text-lg font-bold">Impossibile caricare la dashboard</p>
          <p className="text-sm text-muted-foreground">Controlla la connessione e riprova.</p>
        </div>
        <Button onClick={load} data-testid="dashboard-retry">
          <RefreshCw className="mr-2 h-4 w-4" /> Riprova
        </Button>
      </div>
    );

  if (!data)
    return (
      <div className="flex h-full items-center justify-center" data-testid="dashboard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );

  const k = data.kpis;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">Analisi Vendite e Profili Clienti</h1>
          <p className="text-sm text-muted-foreground">Panoramica clienti e agenzie · {(!period || period === "all") ? "da sempre" : period === "last2" ? "ultimi 2 anni" : period === "m3" ? "ultimi 3 mesi" : period === "m6" ? "ultimi 6 mesi" : period === "m12" ? "ultimi 12 mesi" : period === "custom" ? "periodo personalizzato" : `anno ${period}`}.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={period || "all"} onValueChange={changePeriod}>
            <SelectTrigger className="h-10 w-[200px]" data-testid="dashboard-period-select">
              <SelectValue placeholder="Periodo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="last2">Ultimi 2 anni</SelectItem>
              {(data.trend_years || []).map((y) => (
                <SelectItem key={y} value={String(y)}>Anno {y}</SelectItem>
              ))}
              {!isAgency && (
                <>
                  <SelectItem value="m3">Ultimi 3 mesi</SelectItem>
                  <SelectItem value="m6">Ultimi 6 mesi</SelectItem>
                  <SelectItem value="m12">Ultimi 12 mesi</SelectItem>
                  <SelectItem value="custom">Periodo personalizzato…</SelectItem>
                </>
              )}
              <SelectItem value="all">Da sempre</SelectItem>
            </SelectContent>
          </Select>
          {period === "custom" && (
            <div className="flex items-center gap-1" data-testid="custom-range">
              <Input type="month" value={fromM} onChange={(e) => setFromM(e.target.value)} className="h-10 w-[150px]" data-testid="from-month" />
              <span className="text-muted-foreground">–</span>
              <Input type="month" value={toM} onChange={(e) => setToM(e.target.value)} className="h-10 w-[150px]" data-testid="to-month" />
              <Button variant="outline" className="h-10" onClick={applyCustom} data-testid="apply-range">
                <CalendarRange className="mr-1.5 h-4 w-4" /> Applica
              </Button>
            </div>
          )}
        </div>
      </div>

      {user?.role === "agency" && user?.agency_id && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" className="h-9" onClick={() => setPianoOpen(true)} data-testid="dashboard-piano-btn">
            <Route className="mr-2 h-4 w-4" /> Piano di presidio
          </Button>
        </div>
      )}
      {user?.role === "agency" && <AgencyScorecard />}
      {user?.role === "agency" && user?.agency_id && pianoOpen && (
        <PianoPresidioDialog agencyId={user.agency_id} agencyName={user.agency_name || user.name} onClose={() => setPianoOpen(false)} />
      )}
      {user?.role === "agency" && user?.agency_id && <ImpegniChiusura agencyId={user.agency_id} canEdit />}

      <div className="relative rounded-xl border-2 border-primary/70 p-4 pt-6 space-y-6 sm:p-5 sm:pt-6" data-testid="analisi-vendite-perimetro">
        <span className="absolute -top-3 left-5 z-10 rounded-md bg-primary px-3 py-1 text-xs font-black uppercase tracking-wide text-primary-foreground shadow-md" data-testid="perimetro-label-risultati">Risultati vendite</span>
        {data.yoy_compare && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="yoy-compare">
            <YoySlot
              title="vs anno precedente"
              subtitle={`Progressivo alla data · ${data.yoy_compare.ytd?.label || ""}`}
              block={data.yoy_compare.ytd}
              testid="yoy-ytd"
              period="ytd"
              onOrders={openOrdini}
            />
            <YoySlot
              title="vs stesso mese anno prec."
              subtitle={`Mese di ${data.yoy_compare.month?.label || ""}`}
              block={data.yoy_compare.month}
              testid="yoy-month"
              period="month"
              onOrders={openOrdini}
            />
          </div>
        )}
        <Dialog open={!!ordini} onOpenChange={(o) => !o && setOrdini(null)}>
          <DialogContent className="max-w-2xl" data-testid="ordini-dialog">
            <DialogHeader>
              <DialogTitle>Ordini ricevuti · {ordini?.period === "month" ? "mese corrente" : "anno in corso"}</DialogTitle>
              <DialogDescription>
                {ordini?.loading ? "Caricamento…" : `${ordini?.data?.count ?? 0} ordini · totale ${eur(ordini?.data?.total ?? 0)}${ordini?.data?.can_margin && ordini?.data?.total_margine != null ? ` · margine ${eur(ordini.data.total_margine)} (${ordini.data.margine_pct}%)${ordini.data.orders_no_margine ? ` · ${ordini.data.orders_no_margine} senza margine` : ""}` : ""}`}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto">
              {ordini?.loading ? (
                <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carico gli ordini…</div>
              ) : (ordini?.data?.orders?.length ? (
                <table className="w-full text-sm" data-testid="ordini-table">
                  <thead><tr className="border-b text-left text-[11px] uppercase text-muted-foreground">
                    <th className="px-3 py-2.5">Cliente</th><th className="px-3 py-2.5">Agenzia</th><th className="px-3 py-2.5">Data</th>{ordini?.data?.can_margin && <th className="px-3 py-2.5 text-right">Margine</th>}{ordini?.data?.can_margin && <th className="px-3 py-2.5 text-right">Marg %</th>}<th className="px-3 py-2.5 text-right">Importo</th>
                  </tr></thead>
                  <tbody>
                    {ordini.data.orders.map((o, i) => (
                      <tr key={i} className="border-b border-border/50" data-testid={`ordine-row-${i}`}>
                        <td className="px-3 py-2.5 font-medium">{o.cliente}</td>
                        <td className="px-3 py-2.5 text-muted-foreground">{o.agenzia}</td>
                        <td className="px-3 py-2.5 font-mono text-xs">{fmtOrderDate(o.data)}</td>
                        {ordini.data.can_margin && <td className="px-3 py-2.5 text-right font-mono text-muted-foreground">{o.margine != null ? eur(o.margine) : "n.d."}</td>}
                        {ordini.data.can_margin && <td className="px-3 py-2.5 text-right font-mono text-muted-foreground">{o.margine_pct != null ? `${o.margine_pct}%` : "n.d."}</td>}
                        <td className="px-3 py-2.5 text-right font-mono">{eur(o.importo)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <div className="p-6 text-sm text-muted-foreground">Nessun ordine nel periodo.</div>)}
            </div>
          </DialogContent>
        </Dialog>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <KPI icon={Euro} label="Vendite Totali" value={eur(k.total_revenue)} accent testid="kpi-revenue" />
          <KPI icon={Users} label="Clienti Totali" value={num(k.clienti_totali ?? k.total_partners)} testid="kpi-partners" legend="Tutti i clienti anagrafati (schede totali), indipendentemente dagli ordini." />
          <KPI icon={UserCheck} label="Clienti Attivi" value={num(k.clienti_attivi ?? 0)} testid="kpi-clienti-attivi" legend="Clienti con almeno un ordine reale (vendite Torri) nel periodo selezionato (default: anno in corso)." />
          <KPI icon={Handshake} label="Conversione in Partner" value={`${k.tasso_partner ?? 0}% (${num(k.partner_firmati ?? 0)})`} testid="kpi-tasso-partner" />
          <KPI icon={Percent} label="Copertura da Partner" value={`${k.tasso_copertura ?? 0}%`} testid="kpi-tasso-copertura" />
          {!isAgency && <KPI icon={Building2} label="Agenzie Attive" value={num(k.active_agencies)} testid="kpi-agencies" />}
        </div>

        <Card className="p-6" data-testid="trend-card" ref={trendRef}>
          <AgencyTrendChart trend={data.agency_trend} agencies={data.agencies_list} yoy={data.yoy_trend} yoyYears={data.yoy_years} yoyByAgency={data.yoy_agency_trend} view={trendView} onViewChange={setTrendView} />
        </Card>
      </div>

      <div className="relative space-y-6 rounded-xl border-2 border-primary/70 p-4 pt-6 sm:p-5 sm:pt-6" data-testid="territorio-perimetro">
      <span className="absolute -top-3 left-5 z-10 rounded-md bg-primary px-3 py-1 text-xs font-black uppercase tracking-wide text-primary-foreground shadow-md" data-testid="perimetro-label-agenzie">Risultati agenzie</span>
      <div className="grid grid-cols-1 gap-6">
        <Card className="p-6" data-testid="map-card">
          <div className="mb-4 flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-lg font-bold tracking-tight">Distribuzione sul territorio <span className="font-normal text-muted-foreground">(cumulato)</span></h2>
          </div>
          <ItalyMap />
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card className="p-6" data-testid="agency-territory-card">
          <button onClick={() => setTerrOpen((v) => !v)} className="flex w-full items-center justify-between gap-2" data-testid="territory-section-toggle">
            <div className="flex items-center gap-2">
              <Network className="h-4 w-4 text-primary" />
              <h2 className="font-heading text-lg font-bold tracking-tight">Collegamento Agenzie ↔ Territorio <span className="font-normal text-muted-foreground">(totali)</span></h2>
            </div>
            <ChevronDown className={`h-5 w-5 text-muted-foreground transition-transform ${terrOpen ? "rotate-180" : ""}`} />
          </button>
          {terrOpen && (
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {(data.agency_territory || []).map((t) => {
              const open = !!expandedTerr[t.agency];
              return (
              <div key={t.agency} className="rounded-md border border-border p-4" data-testid={`territory-${t.agency}`}>
                <button onClick={() => setExpandedTerr((s) => ({ ...s, [t.agency]: !s[t.agency] }))} className="w-full text-left" data-testid={`territory-toggle-${t.agency}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: agentColorHex(t.agency) }} />
                      <span className="font-semibold">{t.agency}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="font-mono">{eur(t.revenue)}</Badge>
                      <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{num(t.partners)} clienti collegati · {t.regions.length} regioni</div>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {t.regions.length ? t.regions.map((r) => (
                      <Badge key={r} variant="outline" className="text-[11px]">{r}</Badge>
                    )) : <span className="text-xs text-muted-foreground">Nessuna regione</span>}
                  </div>
                </button>
                {open && (
                  <div className="mt-3 border-t border-border pt-3" data-testid={`territory-detail-${t.agency}`}>
                    <div className="mb-2 text-[11px] font-semibold uppercase text-muted-foreground">Dettaglio per regione</div>
                    {t.by_region && t.by_region.length ? (
                      <div className="space-y-1.5">
                        {t.by_region.map((r) => (
                          <div key={r.region} className="flex items-center justify-between gap-2 text-sm">
                            <span>{r.region}</span>
                            <span className="flex items-center gap-3 font-mono text-xs text-muted-foreground">
                              <span>{num(r.partners)} clienti</span>
                              <span className="text-foreground">{eur(r.revenue)}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : <span className="text-xs text-muted-foreground">Nessun dettaglio disponibile.</span>}
                  </div>
                )}
              </div>
              );
            })}
          </div>
          )}
        </Card>
      </div>
      </div>

      <div className="relative space-y-6 rounded-xl border-2 border-primary/70 p-4 pt-6 sm:p-5 sm:pt-6" data-testid="profili-clienti-perimetro">
      <span className="absolute -top-3 left-5 z-10 rounded-md bg-primary px-3 py-1 text-xs font-black uppercase tracking-wide text-primary-foreground shadow-md" data-testid="perimetro-label-profili">Profili clienti</span>
      <ProfilazioneRete />

      <Card className="p-6" data-testid="quadrant-chart-card">
        <div className="mb-1 flex items-center gap-2">
          <LayoutGrid className="h-4 w-4 text-primary" />
          <h2 className="font-heading text-lg font-bold tracking-tight">Matrice Portafoglio Clienti</h2>
          <span className="ml-auto text-xs text-muted-foreground">{scatterPartners.filter((p) => (p.profilazione || {}).categoria_auto).length} clienti</span>
        </div>
        <p className="mb-2 text-xs text-muted-foreground">Ogni punto è un cliente · asse orizzontale = Attività (engagement), verticale = Valore (potential). I 4 quadranti sono la classificazione. I clienti senza dati strutturali sono posizionati con una <span className="text-amber-400">stima automatica</span> dai dati di vendita (i dati reali migliorano la precisione).</p>
        <QuadrantChart partners={scatterPartners} height={340} />
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <Card className="p-6 lg:col-span-6" data-testid="ranking-card">
          <div className="mb-4 flex items-center gap-2">
            <Trophy className="h-4 w-4 text-primary" />
            <h2 className="font-heading text-lg font-bold tracking-tight">Classifica Clienti</h2>
          </div>
          {(() => {
            const cols = [
              { key: "vesta_punti", label: "Punti Vesta", fmt: (v) => num(v) },
              { key: "importo_totale", label: "Importo", fmt: (v) => eur(v) },
              { key: "kwp_totale", label: "kW", fmt: (v) => num(v) },
              { key: "num_preventivi", label: "Prev.", fmt: (v) => num(v) },
              { key: "num_ordini", label: "Ordini", fmt: (v) => num(v) },
              { key: "share_of_wallet", label: "SoW", fmt: (v) => (v == null || v === "N/D" ? "N/D" : `${v}%`) },
            ];
            const numv = (x) => (typeof x === "number" ? x : x === "N/D" || x == null ? -1 : Number(x) || -1);
            const sorted = [...(data.top_partners || [])].sort((a, b) => (rankSort.dir === "asc" ? 1 : -1) * (numv(a[rankSort.key]) - numv(b[rankSort.key])));
            const toggle = (key) => setRankSort((s) => s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" });
            const gridCls = "grid grid-cols-[2rem_10rem_repeat(6,4.5rem)] items-center gap-2";
            return (
              <div className="overflow-x-auto rounded-md border border-border">
                <div className="min-w-[42rem]">
                  <div className={`${gridCls} border-b border-border bg-muted/50 px-3 py-2 text-[11px] font-semibold uppercase text-muted-foreground`}>
                    <span>#</span>
                    <span>Cliente</span>
                    {cols.map((c) => (
                      <button key={c.key} onClick={() => toggle(c.key)} className="flex items-center justify-end gap-1 text-right hover:text-foreground" data-testid={`rank-sort-${c.key}`}>
                        {c.label}
                        {rankSort.key === c.key ? (rankSort.dir === "desc" ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                      </button>
                    ))}
                  </div>
                  <div className="max-h-[420px] overflow-y-auto">
                    {sorted.map((p, i) => (
                      <div key={p.id} className={`${gridCls} border-b border-border/60 px-3 py-2 last:border-0`} data-testid={`rank-row-${i}`}>
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded font-mono text-xs font-bold ${i < 3 ? "bg-primary text-primary-foreground" : "bg-accent"}`}>{i + 1}</span>
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold">{p.name}</div>
                          <div className="text-[11px] text-muted-foreground">{p.region || "N/D"}</div>
                        </div>
                        {cols.map((c) => (
                          <span key={c.key} className="text-right font-mono text-sm">{c.fmt(p[c.key])}</span>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </Card>

        <Card className="p-6 lg:col-span-6" data-testid="inactive-card">
          <div className="mb-4 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <h2 className="font-heading text-lg font-bold tracking-tight">Clienti inattivi</h2>
          </div>
          {data.inactive_partners.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessun cliente inattivo. Ottimo lavoro!</p>
          ) : (
            <div className="space-y-2">
              {data.inactive_partners.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2" data-testid={`inactive-row-${p.id}`}>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground">{p.agency_name || "Nessuna agenzia"}</div>
                    <div className="mt-0.5 text-[11px] text-muted-foreground" data-testid={`inactive-last-order-${p.id}`}>
                      {p.last_order_date
                        ? <>Ultimo ordine: <span className="font-mono text-foreground">{fmtOrderDate(p.last_order_date)}</span> · <span className="font-mono text-foreground">{eur(p.last_order_importo)}</span> · <span className="font-mono text-foreground">{num(p.last_order_kwp)} kW</span></>
                        : <span className="italic">Nessun ordine registrato</span>}
                    </div>
                  </div>
                  <Badge variant="outline" className="shrink-0 border-destructive/40 text-destructive">
                    {p.days_inactive == null ? "Mai" : `${p.days_inactive}gg`}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      </div>
    </div>
  );
}
